import type { BotType, Slot } from '../../types/game'
import type { HostDraft } from './lobbyDraft'
import { SelectDropdown } from '../shared/SelectDropdown'
import { assetUrl } from '../../utils/assets'

interface HostModalProps {
  alias: string
  draft: HostDraft
  onChange: (draft: HostDraft) => void
  onBack: () => void
  onAdvanced: () => void
  onHost: (opts: {
    name: string
    donationMode: HostDraft['donationMode']
    eggIncubation: HostDraft['eggIncubation']
    incrementalBroodChamberCost: boolean
    infectionSpread: boolean
    survivorFavorMode: HostDraft['survivorFavorMode']
    winCondition: HostDraft['winCondition']
    publicOffers: boolean
    unlimitedQueenTrade: boolean
    slotStates: Record<string, Slot>
    botTypes: Record<string, BotType>
  }) => void
}

const COLORS = ['orange', 'red', 'yellow', 'white'] as const

// mirrors BotType on the backend; min_max matches DEFAULT_BOT_TYPE in settings
export const DEFAULT_BOT_TYPE: BotType = 'min_max'

export const BOT_TYPE_LABELS: Record<BotType, string> = {
  dumb: 'Dumb',
  heuristic: 'Heuristic',
  breeder: 'Breeder',
  builder: 'Builder',
  maker: 'Maker',
  forager: 'Forager',
  pollinator: 'Pollinator',
  fighter: 'Fighter',
  min_max: 'Min Max',
}

export function HostModal({ alias, draft, onChange, onBack, onAdvanced, onHost }: HostModalProps) {
  const randomBotType = (): BotType => {
    const types = Object.keys(BOT_TYPE_LABELS) as BotType[]
    return types[Math.floor(Math.random() * types.length)]
  }

  const handleSlotChange = (color: string, slot: Slot) => {
    // first switch to bot: surprise the host with a random strategy
    const botTypes =
      slot === 'bot' && !draft.botTypes[color]
        ? { ...draft.botTypes, [color]: randomBotType() }
        : draft.botTypes
    onChange({ ...draft, botTypes, slotStates: { ...draft.slotStates, [color]: slot } })
  }

  const handleBotTypeChange = (color: string, botType: BotType) =>
    onChange({ ...draft, botTypes: { ...draft.botTypes, [color]: botType } })

  const handleSubmit = () => {
    const slotStates: Record<string, Slot> = { ...draft.slotStates, orange: 'open' }
    const nonHostColors = COLORS.filter((color) => color !== 'orange')
    const hasJoinableNonHostSlot = nonHostColors.some((color) => {
      const slot = slotStates[color]
      return slot === 'open' || slot === 'bot'
    })

    if (!hasJoinableNonHostSlot) {
      const firstClosedColor = nonHostColors.find((color) => slotStates[color] === 'closed')
      if (firstClosedColor) {
        slotStates[firstClosedColor] = 'bot'
      }
    }

    const botTypes: Record<string, BotType> = {}
    for (const color of nonHostColors) {
      if (slotStates[color] === 'bot') {
        botTypes[color] = draft.botTypes[color] ?? DEFAULT_BOT_TYPE
      }
    }

    onHost({
      name: draft.roomName.trim() || `${alias}'s Game`,
      donationMode: draft.donationMode,
      eggIncubation: draft.eggIncubation,
      incrementalBroodChamberCost: draft.incrementalBroodChamberCost,
      infectionSpread: draft.infectionSpread,
      survivorFavorMode: draft.survivorFavorMode,
      winCondition: draft.winCondition,
      publicOffers: draft.publicOffers,
      unlimitedQueenTrade: draft.unlimitedQueenTrade,
      slotStates,
      botTypes,
    })
  }

  return (
    <>
      <div className="lobby-overlay" />
      <div className="lobby tableau">
        <input
          type="text"
          className="input lobby-title"
          value={draft.roomName}
          onChange={(event) => onChange({ ...draft, roomName: event.target.value })}
          maxLength={50}
        />

        <div className="lobby-slot-list">
          {COLORS.map((color, index) => {
            const isHostSlot = index === 0
            const state = isHostSlot ? 'open' : draft.slotStates[color]
            return (
              <div key={color} className="lobby-slot">
                <div className="lobby-member-header">
                  <div className="opponent-identity">
                    <div
                      className="opponent-marker"
                      style={{ background: color === 'white' ? 'var(--white)' : `var(--${color})` }}
                    />
                    <div className="opponent-id">{color[0].toUpperCase() + color.slice(1)}</div>
                  </div>
                  <div className="lobby-robot-checkbox">
                    {isHostSlot ? (
                      <div className="lobby-slot-badge host">
                        <img src={assetUrl('images', 'host.svg')} alt="Host" className="host-icon" />
                        <div>Host</div>
                      </div>
                    ) : (
                      <div className="host-slot-options">
                        {(['open', 'closed', 'bot'] as Slot[]).map((slot) => (
                          <label key={slot}>
                            <input
                              type="radio"
                              name={`slot-${color}`}
                              checked={state === slot}
                              onChange={() => handleSlotChange(color, slot)}
                            />
                            <span>{slot[0].toUpperCase() + slot.slice(1)}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {!isHostSlot && state === 'bot' ? (
                  <SelectDropdown
                    value={draft.botTypes[color] ?? DEFAULT_BOT_TYPE}
                    options={(Object.keys(BOT_TYPE_LABELS) as BotType[]).map((botType) => ({
                      value: botType,
                      label: BOT_TYPE_LABELS[botType],
                    }))}
                    onChange={(botType) => handleBotTypeChange(color, botType)}
                    ariaLabel={`Bot type for ${color}`}
                  />
                ) : (
                  <input
                    type="text"
                    className="input"
                    value={isHostSlot ? alias : state[0].toUpperCase() + state.slice(1)}
                    disabled
                    readOnly
                  />
                )}
              </div>
            )
          })}
        </div>

        <div className="lobby-start-container">
          <div id="host-advanced-settings-button" className="lobby-start" onClick={onAdvanced}>
            Advanced Settings
          </div>
          <div className="host-action-buttons">
            <div id="host-back-button" className="lobby-start" onClick={onBack}>
              Back
            </div>
            <div
              id="host-button"
              className={`lobby-start${draft.roomName.trim() ? '' : ' disabled'}`}
              onClick={draft.roomName.trim() ? handleSubmit : undefined}
            >
              Host
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
